package com.routexk.quiz.utils

import com.routexk.quiz.core.config.AppConfig
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLScriptElement
import kotlin.js.Promise

external interface GoogleUser {
    val email: String
    val name: String?
    val picture: String?
    val sub: String
}

external interface GoogleCredentialResponse {
    val credential: String
    val select_by: String?
}

external interface GoogleInitConfig {
    val client_id: String
    val callback: (GoogleCredentialResponse) -> Unit
    val auto_select: Boolean?
    val cancel_on_tap_outside: Boolean?
}

external interface GoogleRenderButtonConfig {
    val theme: String?
    val size: String?
    val type: String?
    val shape: String?
    val text: String?
    val logo_alignment: String?
}

external interface GoogleAccounts {
    val id: GoogleId
}

external interface GoogleId {
    fun initialize(config: GoogleInitConfig)
    fun renderButton(parent: dynamic, options: GoogleRenderButtonConfig)
    fun prompt()
    fun disableAutoSelect()
}

@JsName("google")
external val google: GoogleAccounts

external interface AppleUser {
    val email: String?
    val name: AppleUserName?
}

external interface AppleUserName {
    val firstName: String?
    val lastName: String?
}

external interface AppleAuthorizationResponse {
    val authorization: AppleAuthorization
    val user: AppleUser?
}

external interface AppleAuthorization {
    val code: String
    val id_token: String
    val state: String?
}

external interface AppleInitConfig {
    val clientId: String
    val scope: String
    val redirectURI: String
    val state: String?
    val nonce: String?
    val usePopup: Boolean?
}

external interface AppleAuth {
    fun init(config: AppleInitConfig)
    fun signIn(): Promise<AppleAuthorizationResponse>
}

external interface AppleIDType {
    val auth: AppleAuth
}

@JsName("AppleID")
external val AppleID: AppleIDType?

object OAuthHelper {
    private val GOOGLE_CLIENT_ID: String
        get() = AppConfig.googleClientId

    private val APPLE_CLIENT_ID: String
        get() = AppConfig.appleClientId

    private var isGoogleInitialized = false
    private var isPromptActive = false

    private fun handleGoogleCredentialResponse(
        response: dynamic,
        onSuccess: (GoogleUser) -> Unit,
        onError: (String) -> Unit
    ) {
        val error = response.error as? String
        if (error != null) {
            console.error("Google OAuth error:", error)
            onError("Google OAuth error: $error")
            return
        }

        val credential = response.credential as? String
        if (credential != null) {
            parseJWT(credential)?.let { user ->
                onSuccess(user)
            } ?: onError("Failed to parse user data")
        } else {
            console.error("No credential in response:", response)
            onError("No credential in response")
        }
    }

    private fun setGoogleCallback(onSuccess: (GoogleUser) -> Unit, onError: (String) -> Unit) {
        window.asDynamic().GOOGLE_CLIENT_ID = GOOGLE_CLIENT_ID
        window.asDynamic().handleGoogleCallback = { response: dynamic ->
            handleGoogleCredentialResponse(response, onSuccess, onError)
        }
    }

    fun loadGoogleSDK(onLoaded: () -> Unit) {
        if (isGoogleInitialized && js("typeof google !== 'undefined'") as Boolean) {
            onLoaded()
            return
        }

        val existingScript = document.getElementById("google-sdk")
        if (existingScript != null && js("typeof google !== 'undefined'") as Boolean) {
            isGoogleInitialized = true
            onLoaded()
            return
        }

        if (existingScript != null) {
            var checkIntervalId: Int? = null
            checkIntervalId = window.setInterval({
                if (js("typeof google !== 'undefined'") as Boolean) {
                    checkIntervalId?.let { window.clearInterval(it) }
                    isGoogleInitialized = true
                    onLoaded()
                }
            }, 100)
            return
        }

        val script = document.createElement("script") as HTMLScriptElement
        script.id = "google-sdk"
        script.src = "https://accounts.google.com/gsi/client"
        script.async = true
        script.onload = {
            var attempts = 0
            val maxAttempts = 50
            var checkIntervalId: Int? = null
            checkIntervalId = window.setInterval({
                attempts++
                if (js("typeof google !== 'undefined'") as Boolean) {
                    checkIntervalId?.let { window.clearInterval(it) }
                    isGoogleInitialized = true
                    onLoaded()
                } else if (attempts >= maxAttempts) {
                    checkIntervalId?.let { window.clearInterval(it) }
                    console.error("Google SDK failed to load")
                }
            }, 100)
        }
        script.asDynamic().onerror = {
            console.error("Failed to load Google SDK script")
        }
        document.head?.appendChild(script)
    }

    fun initializeGoogle(onSuccess: (GoogleUser) -> Unit, onError: (String) -> Unit) {
        loadGoogleSDK {
            try {
                setGoogleCallback(onSuccess, onError)

                js("""
                    (function() {
                        if (typeof window.google === 'undefined' ||
                            typeof window.google.accounts === 'undefined' ||
                            typeof window.google.accounts.id === 'undefined') {
                            console.error('Google Identity Services not loaded');
                            return false;
                        }

                        if (!window.__googleInitialized) {
                            window.google.accounts.id.initialize({
                                client_id: window.GOOGLE_CLIENT_ID,
                                callback: function(response) {
                                    if (window.handleGoogleCallback) {
                                        window.handleGoogleCallback(response);
                                    }
                                },
                                auto_select: false,
                                cancel_on_tap_outside: true
                            });
                            window.__googleInitialized = true;
                        }

                        if (!window.__googlePromptActive) {
                            window.__googlePromptActive = true;
                            setTimeout(function() {
                                try {
                                    window.google.accounts.id.prompt();
                                } catch (e) {
                                    console.error('Error showing prompt:', e);
                                    window.__googlePromptActive = false;
                                }
                                setTimeout(function() {
                                    window.__googlePromptActive = false;
                                }, 2000);
                            }, 100);
                        }
                        return true;
                    })();
                """)
            } catch (e: Throwable) {
                console.error("Google initialization error:", e)
                onError(e.message ?: "Google initialization failed")
            }
        }
    }

    fun promptGoogleOneTap() {
        try {
            if (!isPromptActive && js("typeof google !== 'undefined' && google.accounts && google.accounts.id") as Boolean) {
                isPromptActive = true
                google.id.prompt()
                window.setTimeout({
                    isPromptActive = false
                }, 1000)
            }
        } catch (e: Throwable) {
            isPromptActive = false
            console.error("Failed to prompt Google One Tap:", e)
        }
    }

    fun signInWithGoogleButton(
        buttonElementId: String,
        onSuccess: (GoogleUser) -> Unit,
        onError: (String) -> Unit,
        size: String = "large"
    ) {
        loadGoogleSDK {
            try {
                setGoogleCallback(onSuccess, onError)
                window.asDynamic().GOOGLE_BUTTON_ELEMENT_ID = buttonElementId
                window.asDynamic().GOOGLE_BUTTON_SIZE = size

                js("""
                    (function() {
                        if (typeof window.google === 'undefined' ||
                            typeof window.google.accounts === 'undefined' ||
                            typeof window.google.accounts.id === 'undefined') {
                            console.error('Google Identity Services not loaded');
                            return false;
                        }

                        var buttonElement = document.getElementById(window.GOOGLE_BUTTON_ELEMENT_ID);
                        if (!buttonElement) {
                            console.error('Button element not found:', window.GOOGLE_BUTTON_ELEMENT_ID);
                            return false;
                        }

                        window.google.accounts.id.initialize({
                            client_id: window.GOOGLE_CLIENT_ID,
                            callback: function(response) {
                                if (window.handleGoogleCallback) {
                                    window.handleGoogleCallback(response);
                                }
                            }
                        });

                        window.google.accounts.id.renderButton(buttonElement, {
                            theme: 'outline',
                            size: window.GOOGLE_BUTTON_SIZE || 'large',
                            type: 'standard',
                            text: 'signin_with',
                            shape: 'rectangular'
                        });

                        return true;
                    })();
                """)
            } catch (e: Throwable) {
                console.error("Google button initialization error:", e)
                onError(e.message ?: "Google button initialization failed")
            }
        }
    }

    fun triggerGoogleSignIn(
        onSuccess: (GoogleUser) -> Unit,
        onError: (String) -> Unit
    ) {
        loadGoogleSDK {
            try {
                setGoogleCallback(onSuccess, onError)

                js("""
                    (function() {
                        if (typeof window.google === 'undefined' ||
                            typeof window.google.accounts === 'undefined' ||
                            typeof window.google.accounts.id === 'undefined') {
                            console.error('Google Identity Services not loaded');
                            return false;
                        }

                        var tempButton = document.createElement('div');
                        tempButton.id = 'temp-google-signin-' + Date.now();
                        tempButton.style.display = 'none';
                        document.body.appendChild(tempButton);

                        if (!window.__googleInitialized) {
                            window.google.accounts.id.initialize({
                                client_id: window.GOOGLE_CLIENT_ID,
                                callback: function(response) {
                                    if (window.handleGoogleCallback) {
                                        window.handleGoogleCallback(response);
                                    }
                                }
                            });
                            window.__googleInitialized = true;
                        }

                        window.google.accounts.id.renderButton(tempButton, {
                            theme: 'outline',
                            size: 'large',
                            type: 'standard',
                            text: 'signin_with',
                            shape: 'rectangular'
                        });

                        setTimeout(function() {
                            var button = tempButton.querySelector('div[role="button"]');
                            if (button) {
                                button.click();
                            } else {
                                console.error('Google button not found');
                            }

                            setTimeout(function() {
                                if (tempButton.parentNode) {
                                    tempButton.parentNode.removeChild(tempButton);
                                }
                            }, 1000);
                        }, 100);

                        return true;
                    })();
                """)
            } catch (e: Throwable) {
                console.error("Google sign-in trigger error:", e)
                onError(e.message ?: "Google sign-in trigger failed")
            }
        }
    }

    fun disableAutoSelect() {
        try {
            js("""
                if (typeof window.google !== 'undefined' &&
                    window.google.accounts &&
                    window.google.accounts.id &&
                    typeof window.google.accounts.id.disableAutoSelect === 'function') {
                    window.google.accounts.id.disableAutoSelect();
                }
            """)
        } catch (e: Throwable) {
            console.error("Failed to disable auto-select:", e)
        }
    }

    fun resetGoogleSession() {
        disableAutoSelect()
        document.cookie = "g_state=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"
        document.cookie = "g_enabled=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"
    }

    private fun parseAppleJWT(token: String): dynamic? {
        return try {
            val base64Url = token.split(".")[1]
            val base64 = base64Url.replace('-', '+').replace('_', '/')
            val jsonPayload = js("decodeURIComponent(atob(base64).split('').map(function(c) { return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2); }).join(''))")
            JSON.parse<dynamic>(jsonPayload as String)
        } catch (e: Throwable) {
            console.error("Failed to parse Apple JWT:", e)
            null
        }
    }

    private fun parseJWT(token: String): GoogleUser? {
        return try {
            val base64Url = token.split(".")[1]
            val base64 = base64Url.replace('-', '+').replace('_', '/')
            val jsonPayload = js("decodeURIComponent(atob(base64).split('').map(function(c) { return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2); }).join(''))")
            val payload = JSON.parse<dynamic>(jsonPayload as String)

            object : GoogleUser {
                override val email = payload.email as String
                override val name = payload.name as? String
                override val picture = payload.picture as? String
                override val sub = payload.sub as String
            }
        } catch (e: Throwable) {
            console.error("Failed to parse JWT:", e)
            null
        }
    }

    fun signInWithApple(
        onSuccess: (email: String, name: String?, providerId: String) -> Unit,
        onError: (String) -> Unit
    ) {
        loadAppleSDK {
            try {
                val appleId = AppleID
                if (appleId == null) {
                    onError("Apple Sign In SDK not loaded")
                    return@loadAppleSDK
                }

                appleId.auth.init(object : AppleInitConfig {
                    override val clientId = APPLE_CLIENT_ID
                    override val scope = "name email"

                    override val redirectURI = "${kotlinx.browser.window.location.origin}/auth/apple/callback"
                    override val state = null
                    override val nonce = null
                    override val usePopup = true
                })

                appleId.auth.signIn()
                    .then { response ->

                        val email = response.user?.email ?: ""

                        val fullName = response.user?.name?.let { name ->
                            "${name.firstName ?: ""} ${name.lastName ?: ""}".trim()
                        }

                        val idToken = response.authorization.id_token
                        val providerId = parseAppleJWT(idToken)?.sub ?: idToken

                        if (email.isEmpty() && providerId.isEmpty()) {
                            onError("Failed to get user information from Apple")
                        } else {
                            onSuccess(email, fullName, providerId)
                        }
                    }
                    .catch { error ->
                        val errorMessage = error.toString()
                        console.error("Apple Sign-In error:", error)
                        onError(errorMessage.ifEmpty { "Apple sign in failed" })
                    }
            } catch (e: Throwable) {
                onError(e.message ?: "Apple sign in failed")
            }
        }
    }

    fun loadAppleSDK(onLoaded: () -> Unit) {
        val existingScript = document.getElementById("apple-sdk")
        if (existingScript != null) {
            onLoaded()
            return
        }

        val script = document.createElement("script") as HTMLScriptElement
        script.id = "apple-sdk"
        script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
        script.async = true
        script.onload = { onLoaded() }
        document.head?.appendChild(script)
    }
}
